import React, { useCallback, useEffect, useState } from "react";
import {
  getSchedulingErrorMessage,
  getSchedulingSettings,
  listProviders,
  listServices,
} from "../../../api/schedulingApi";
import AppointmentsPanel from "./AppointmentsPanel";
import CustomersPanel from "./CustomersPanel";
import ProvidersPanel from "./ProvidersPanel";
import ServicesPanel from "./ServicesPanel";
import SettingsPanel from "./SettingsPanel";
import { Alert } from "./ui";

const SECTIONS = [
  ["appointments", "Appointments"],
  ["providers", "Providers & hours"],
  ["services", "Services"],
  ["customers", "Customers"],
  ["settings", "Settings"],
];

export default function SchedulingTab({ botId }) {
  const [section, setSection] = useState("appointments");
  const [settings, setSettings] = useState(null);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [error, setError] = useState("");

  const loadProviders = useCallback(async () => setProviders((await listProviders(botId)) || []), [botId]);
  const loadServices = useCallback(async () => setServices((await listServices(botId)) || []), [botId]);

  useEffect(() => {
    let active = true;
    Promise.all([getSchedulingSettings(botId), listProviders(botId), listServices(botId)])
      .then(([s, p, sv]) => {
        if (!active) return;
        setSettings(s);
        setProviders(p || []);
        setServices(sv || []);
      })
      .catch((err) => active && setError(getSchedulingErrorMessage(err, "Could not load scheduling data.")));
    return () => {
      active = false;
    };
  }, [botId]);

  if (error) return <Alert>{error}</Alert>;
  if (!settings) return <p className="text-sm text-slate-500">Loading scheduling…</p>;

  const timeZone = settings.timezone || "UTC";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Scheduling sections">
        {SECTIONS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={section === key}
            onClick={() => setSection(key)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
              section === key ? "bg-[#096dea] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {section === "appointments" && <AppointmentsPanel botId={botId} timeZone={timeZone} providers={providers} services={services} />}
      {section === "providers" && <ProvidersPanel botId={botId} providers={providers} services={services} onProvidersChanged={loadProviders} />}
      {section === "services" && <ServicesPanel botId={botId} services={services} onServicesChanged={loadServices} />}
      {section === "customers" && <CustomersPanel botId={botId} timeZone={timeZone} providers={providers} />}
      {section === "settings" && <SettingsPanel botId={botId} settings={settings} onSaved={setSettings} />}
    </div>
  );
}
